import { createContext, useContext } from 'react';
import type { InstitutionRef, Me, ModuleInfo, Person, Role } from './types';

export interface SessionValue {
  me: Me;
  user: Person;
  role: Role;
  isAdmin: boolean;
  /** Admins and coordinators: release quizzes, approve teachers, manage teachers. */
  isManager: boolean;
  /** Admins, coordinators and teachers: manage content, grade, see reports. */
  isStaff: boolean;
  /** Modules switched on. */
  modules: ModuleInfo[];
  /** Every installed module (admins only; others get the enabled ones). */
  allModules: ModuleInfo[];
  hasModule: (key: string) => boolean;
  /** Institutions whose data this person may look at (admins: all of them). */
  institutions: InstitutionRef[];
  /** The one being looked at; null = every institution (admins who chose "Todas"). */
  institution: InstitutionRef | null;
  /** Switches the institution (an id, or ALL_INSTITUTIONS for admins). */
  setInstitution: (id: string) => void;
  /** TEST_EMAILS: may switch between student and teacher. */
  testAccount: boolean;
  /** Test accounts: back to the "enter as student or teacher" screen. */
  changeTestProfile: () => void;
  /** Reloads /me, e.g. after switching a module on/off or editing the profile. */
  refresh: () => Promise<void>;
}

export const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>.');
  return value;
}
